import { Turnstile, type TurnstileInstance } from "@marsidev/react-turnstile"
import { motion } from "motion/react"
import { type FC, useRef, useState } from "react"
import { useNavigate, useSearchParams } from "react-router"
import { getCookie } from "react-use-cookie"

import { getPasswordValidationError, passwordRequirementsText } from "@forento/core/utilities/password"
import { toNullIfEmpty } from "@forento/core/utilities/string"
import Form from "@forento/ui/components/Form"

import Button, { SubmitButton } from "~/components/Button"
import GoogleSigninButton from "~/components/GoogleSigninButton"
import InputField from "~/components/InputField"
import Link from "~/components/Link"
import PartialLoadingPage from "~/components/PartialLoadingPage"
import { useUser } from "~/contexts/UserContext"
import routes from "~/utilities/routes"
import { query } from "~/utilities/trpc"

import { authCardClass, staggerContainer, staggerItem } from "../../Layout/authCard"
import { signup } from ".."
import ReferralCodeModal from "./ReferralCodeModal"

const Regular: FC = () => {
	const user = useUser()
	const navigate = useNavigate()
	const [searchParams] = useSearchParams()

	const turnstileRef = useRef<TurnstileInstance | null>(null)

	const [firstName, setFirstName] = useState("")
	const [lastName, setLastName] = useState("")
	const [email, setEmail] = useState("")
	const [password, setPassword] = useState("")
	const [passwordConfirmation, setPasswordConfirmation] = useState("")

	const [isReferralCodeModalOpen, setReferralCodeModalOpen] = useState(false)
	const [referralCode, setReferralCode] = useState(searchParams.get("r"))
	const referrer = query.referral.getCode.useQuery(referralCode!, { enabled: referralCode !== null })
	const [error, setError] = useState<string>()
	const [isSubmitting, setSubmitting] = useState(false)

	const nextUrl = searchParams.get("next")

	async function handleSignup() {
		const passwordError = getPasswordValidationError(password)
		if (passwordError !== null) {
			setError(passwordError)
			return
		}

		const captchaToken = turnstileRef.current?.getResponse()
		if (!captchaToken) {
			setError("Please complete the captcha verification.")
			return
		}

		setSubmitting(true)
		setError(undefined)
		const result = await signup({
			firstName,
			lastName,
			email,
			password,
			passwordConfirmation,
			referralCode: referrer?.data?.code,
			source: searchParams.get("s") ?? toNullIfEmpty(getCookie("s")) ?? undefined,
			captchaToken,
		})
		setSubmitting(false)
		if (result.status !== "success") {
			setError(result.error)
			turnstileRef.current?.reset()
			return
		}

		await user.reload({ clearSessionCache: true })

		navigate(routes.account.createPlatform())
	}

	return (
		<>
			<ReferralCodeModal
				isOpen={isReferralCodeModalOpen}
				onClose={() => setReferralCodeModalOpen(false)}
				onSubmit={setReferralCode}
			/>
			<Form onSubmit={handleSignup} className={authCardClass}>
				{referrer.error ? (
					<p className="text-danger text-sm">Failed to check for referral code.</p>
				) : referralCode !== null && referrer.isPending ? (
					<PartialLoadingPage />
				) : (
					<motion.div
						className="flex w-full flex-col items-stretch gap-0"
						variants={staggerContainer}
						initial="hidden"
						animate="visible"
					>
						<motion.div variants={staggerItem}>
							<h1 className="text-left text-xl font-bold tracking-tight text-zinc-900">
								Create your account
							</h1>
							<p className="mt-1.5 text-left text-sm text-zinc-500">Get started in a few seconds.</p>
						</motion.div>

						{error !== undefined && (
							<motion.p
								variants={staggerItem}
								className="mt-6 rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700"
							>
								{error}
							</motion.p>
						)}

						<motion.div variants={staggerItem} className="mt-6 grid grid-cols-2 gap-3">
							<InputField
								label="First name"
								value={firstName}
								onChange={setFirstName}
								autoComplete="given-name"
							/>
							<InputField
								label="Last name"
								value={lastName}
								onChange={setLastName}
								autoComplete="family-name"
							/>
						</motion.div>

						<motion.div variants={staggerItem} className="mt-4">
							<InputField
								inputType="email"
								label="Email"
								value={email}
								onChange={setEmail}
								autoComplete="email"
							/>
						</motion.div>

						<motion.div variants={staggerItem} className="mt-4">
							<InputField
								inputType="password"
								label="Password"
								value={password}
								onChange={setPassword}
								autoComplete="new-password"
							/>
							<p className="mt-2 text-left text-xs leading-relaxed text-zinc-500">
								{passwordRequirementsText}
							</p>
						</motion.div>

						<motion.div variants={staggerItem} className="mt-4">
							<InputField
								inputType="password"
								label="Confirm password"
								value={passwordConfirmation}
								onChange={setPasswordConfirmation}
								autoComplete="new-password"
							/>
						</motion.div>

						<motion.div variants={staggerItem} className="mt-4">
							{referrer.data ? (
								<p className="text-sm text-zinc-600">
									Referred by:{" "}
									<span className="font-medium text-zinc-800">{referrer.data.label}</span>
								</p>
							) : (
								<Button
									variant="link"
									onClick={() => setReferralCodeModalOpen(true)}
									className="cursor-pointer text-sm text-zinc-500 underline-offset-2 hover:text-zinc-900"
								>
									Add referral code
								</Button>
							)}
						</motion.div>

						<motion.div variants={staggerItem} className="mt-4">
							<Turnstile
								ref={turnstileRef}
								siteKey={CONFIG.turnstileSiteKey}
								className="flex justify-center"
							/>
						</motion.div>

						<motion.p
							variants={staggerItem}
							className="mt-5 text-left text-xs leading-relaxed text-zinc-500"
						>
							By signing up you agree to our{" "}
							<a
								href="https://forento.io/policy/terms-of-service"
								target="_blank"
								rel="noreferrer noopener"
								className="text-zinc-600 hover:text-zinc-900"
							>
								Terms of Service
							</a>
							.
						</motion.p>

						<motion.div variants={staggerItem} className="mt-5">
							<SubmitButton
								variant="primary"
								isLoading={isSubmitting}
								className="w-full cursor-pointer rounded-xl py-3 text-sm font-semibold"
							>
								Sign up
							</SubmitButton>
						</motion.div>

						<motion.div variants={staggerItem} className="flex items-center gap-3 py-4">
							<div className="h-px flex-1 bg-zinc-200" />
							<span className="text-xs text-zinc-400">or</span>
							<div className="h-px flex-1 bg-zinc-200" />
						</motion.div>

						<motion.div variants={staggerItem} className="flex justify-center">
							<GoogleSigninButton text="signup" />
						</motion.div>

						<motion.p variants={staggerItem} className="mt-5 text-center text-sm text-zinc-500">
							Already have an account?{" "}
							<Link
								to={
									nextUrl
										? `${routes.account.signin()}?next=${encodeURIComponent(nextUrl)}`
										: routes.account.signin()
								}
								className="text-primary hover:text-primary/80 font-medium transition-colors"
							>
								Sign in
							</Link>
						</motion.p>
					</motion.div>
				)}
			</Form>
		</>
	)
}

export default Regular
